import {
  Button,
  Card,
  Col,
  Form,
  Input,
  Row,
  Spin,
  Upload,
  message,
  App,
  Select
} from "antd";
import {
  LeftOutlined,
  Loading3QuartersOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import { ADD_ANNOUNCEMENT } from "../../apis/apis";
import { useNavigate } from "react-router-dom";
import CulsightPageLoader from "../../components/CulsightPageLoader";
import CustomTextEditor from "../../components/CustomTextEditor";

export default function AddAnnouncement() {
  const { notification } = App.useApp();
  const navigate = useNavigate();
  const [form] = Form.useForm();

  // ✅ loaders
  const [pageLoading, setPageLoading] = useState(true);
  const [submitLoading, setSubmitLoading] = useState(false);

  // ✅ image states
  const [imagePreview, setImagePreview] = useState("");
  const [imageFile, setImageFile] = useState(null);
  const [imageError, setImageError] = useState("");

  // ✅ backend errors
  const [errors, setErrors] = useState({});

  // ✅ clear backend error
  const clearError = (field) => {
    setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  useEffect(() => {
    const t = setTimeout(() => setPageLoading(false), 200);
    return () => clearTimeout(t);
  }, []);

  // ✅ image validation (DIMENSION + TYPE + SIZE)
  const handleImageUpload = (file) => {
    if (!["image/jpeg", "image/png", "image/jpg"].includes(file.type)) {
      setImageError("Only JPG / PNG images allowed");
      return false;
    }

    if (file.size > 2 * 1024 * 1024) {
      setImageError("Image must be ≤ 2MB");
      return false;
    }

    const img = new Image();
    img.src = URL.createObjectURL(file);

    img.onload = () => {
      if (img.width === 600 && img.height === 400) {
        setImageError("");
        setImagePreview(URL.createObjectURL(file));
        setImageFile(file);
      } else {
        setImageError("Image must be exactly 600×400 px");
        setImagePreview("");
        setImageFile(null);
      }
    };

    return false; // ✅ stop auto upload
  };

  // ✅ submit (BACKEND VALIDATION PRIMARY)
  const onFinish = async () => {
    try {
      setSubmitLoading(true);

      const FD = new FormData();
      const values = form.getFieldsValue();

      Object.keys(values).forEach((key) => {
        FD.append(key, values[key] ?? "");
      });

      // ✅ ALWAYS SEND IMAGE KEY
      if (imageFile) {
        FD.append("image", imageFile);
      } else {
        FD.append("image", "");
      }

      const res = await ADD_ANNOUNCEMENT(FD);

      if (res?.data?.status) {
        notification.success({
          message: "Successful",
          description: res?.data?.message,
        });
        navigate("/announcements");
      } else {
        setErrors(res?.data?.errors || {});
        setSubmitLoading(false);
        window.scrollTo({ top: 0, behavior: "smooth" });
      }
    } catch (err) {
      setSubmitLoading(false);
      message.error("Server Error, Try Later!");
    }
  };

  const uploadButton = (
    <button type="button" style={{ border: 0, background: "none", }}>
      {pageLoading ? <Loading3QuartersOutlined /> : <PlusOutlined />}
      <div style={{ marginTop: 8, }}>Upload</div>
    </button>
  );

  return (
    <div className="lms-body">
      <Card>
        <Row>
          <Col span={12}>
            <h2>
              <LeftOutlined
                style={{ cursor: "pointer" }}
                onClick={() => navigate("/announcements")}
              />{" "}
              Add Announcement
            </h2>
          </Col>
        </Row>

        {pageLoading ? (
          <CulsightPageLoader />
        ) : (
          <Form
            form={form}
            layout="vertical"
            onFinish={onFinish}
            autoComplete="off"
          >
            {/* ✅ IMAGE */}
            <Form.Item
              label="Upload Thumbnail"
              validateStatus={imageError ? "error" : errors.image ? "error" : ""}
              help={imageError || errors.image || ""}

            >
              <Upload
                listType="picture-card"
                showUploadList={false}
                beforeUpload={handleImageUpload}
              >
                {imagePreview ? (
                  <img
                    src={imagePreview}
                    alt="thumbnail"
                    style={{ width: "100%", objectFit: "cover", }}
                  />
                ) : (
                  uploadButton
                )}
              </Upload>
              <p style={{ color: "#65e7c4" }}>
                Note: Image must be JPG/PNG, ≤2MB and exactly 600×400 px
              </p>
            </Form.Item>

            {/* ✅ BASIC INFO */}
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Title"
                  name="title"
                  validateStatus={errors.title ? "error" : ""}
                  help={errors.title}
                >
                  <Input onChange={() => clearError("title")} />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="Short Description"
                  name="short_description"
                  validateStatus={errors.short_description ? "error" : ""}
                  help={errors.short_description}
                >
                  <Input onChange={() => clearError("short_description")} />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="Tags"
                  name="tags"
                  validateStatus={errors?.tags ? "error" : ""}
                  help={errors?.tags}
                >
                  <Select
                    mode="tags"
                    placeholder="Add tags"
                    style={{ width: "100%" }}
                    onChange={(value) => {
                      clearError("tags");   // error clear karega
                      form.setFieldsValue({ tags: value }); // form me value set karega
                    }}
                  />
                </Form.Item>

              </Col>

              <Col span={12}>
                <Form.Item
                  label="Slug"
                  name="slug"
                  validateStatus={errors?.slug ? "error" : ""}
                  help={errors?.slug}
                >
                  <Input onChange={() => clearError("slug")} placeholder="Enter slug" />
                </Form.Item>
              </Col>
            </Row>

            {/* ✅ DESCRIPTION */}
            <Form.Item
              label="Description"
              name="description"
              validateStatus={errors.description ? "error" : ""}
              help={errors.description}
            >
              <CustomTextEditor
                height={300}
                onChange={(val) => {
                  form.setFieldValue("description", val);
                  clearError("description");
                }}
              />
            </Form.Item>

            {/* ✅ SEO */}
            <h3>SEO Information</h3>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="SEO Title"
                  name="seo_title"
                  validateStatus={errors.seo_title ? "error" : ""}
                  help={errors.seo_title}
                >
                  <Input onChange={() => clearError("seo_title")} />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="SEO Description"
                  name="seo_description"
                  validateStatus={errors.seo_description ? "error" : ""}
                  help={errors.seo_description}
                >
                  <Input onChange={() => clearError("seo_description")} />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="SEO Keywords"
                  name="seo_keywords"
                  validateStatus={errors?.seo_keywords ? "error" : ""}
                  help={errors?.seo_keywords}
                >
                  <Select
                    mode="tags"
                    placeholder="Add SEO keywords"
                    style={{ width: "100%" }}
                    onChange={(value) => {
                      clearError("seo_keywords");
                      form.setFieldsValue({ seo_keywords: value });
                    }}
                  />
                </Form.Item>

              </Col>
            </Row>

            {/* ✅ OTHER INFO */}
            <h3>Other Information</h3>
            <Row gutter={16}>
              <Col span={12}>
                <Form.Item
                  label="Location"
                  name="location"
                  validateStatus={errors.location ? "error" : ""}
                  help={errors.location}
                >
                  <Input onChange={() => clearError("location")} />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="Video URL"
                  name="video_url"
                  validateStatus={errors.video_url ? "error" : ""}
                  help={errors.video_url}
                >
                  <Input onChange={() => clearError("video_url")} />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="Source"
                  name="source"
                  validateStatus={errors.source ? "error" : ""}
                  help={errors.source}
                >
                  <Input onChange={() => clearError("source")} />
                </Form.Item>
              </Col>

              <Col span={12}>
                <Form.Item
                  label="Priority"
                  name="priority"
                  validateStatus={errors.priority ? "error" : ""}
                  help={errors.priority}
                >
                  <Input onChange={() => clearError("priority")} />
                </Form.Item>
              </Col>
            </Row>

            {/* ✅ SUBMIT */}
            <Form.Item>
              <Button
                type="primary"
                htmlType="submit"
                loading={submitLoading}
                style={{ float: "right" }}
              >
                Save
              </Button>
            </Form.Item>
          </Form>
        )}
      </Card>
    </div>
  );
}
